import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import classNames from "classnames";
import { nanoid } from "nanoid";
import {
  createSponsorClick,
  createSponsorImpression,
  trackAdPixel,
} from "core/utils/tracker";

const PREFIX = "SponsorVideoCtaButton";

const classes = {
  root: `${PREFIX}-root`,
  topLeft: `${PREFIX}-topLeft`,
  topRight: `${PREFIX}-topRight`,
  bottomLeft: `${PREFIX}-bottomLeft`,
  bottomRight: `${PREFIX}-bottomRight`,
  topLeftWithAdLabel: `${PREFIX}-topLeftWithAdLabel`,
  topRightWithClose: `${PREFIX}-topRightWithClose`,
};

const CREATIVE_TYPE = "Video";
const CLICKTHROUGH = "CLICKTHROUGH";
const TOP_LEFT = "TOP_LEFT";
const TOP_RIGHT = "TOP_RIGHT";
const BOTTOM_LEFT = "BOTTOM_LEFT";
const BOTTOM_RIGHT = "BOTTOM_RIGHT";

const POSITION_MAP = {
  ["top_left"]: TOP_LEFT,
  ["top_right"]: TOP_RIGHT,
  ["bottom_left"]: BOTTOM_LEFT,
  ["bottom_right"]: BOTTOM_RIGHT,
};

const StyledButton = styled(Button)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: "absolute",
    zIndex: 100001,
    pointerEvents: "auto",
  },

  [`&.${classes.topLeft}`]: {
    top: theme.spacing(1),
    left: theme.spacing(1),

    ["@media (min-width: 375px)"]: {
      top: theme.spacing(2),
      left: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      top: theme.spacing(3),
      left: theme.spacing(3),
    },

    [`&.${classes.topLeftWithAdLabel}`]: {
      top: 48,
    },
  },

  [`&.${classes.topRight}`]: {
    top: theme.spacing(1),
    right: theme.spacing(1),

    ["@media (min-width: 375px)"]: {
      top: theme.spacing(2),
      right: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      top: theme.spacing(3),
      right: theme.spacing(3),
    },

    [`&.${classes.topRightWithClose}`]: {
      top: 56,
    },
  },

  [`&.${classes.bottomLeft}`]: {
    bottom: 56,
    left: theme.spacing(1),

    ["@media (min-width: 375px)"]: {
      left: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      left: theme.spacing(3),
    },
  },

  [`&.${classes.bottomRight}`]: {
    bottom: 56,
    right: theme.spacing(1),

    ["@media (min-width: 375px)"]: {
      right: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      right: theme.spacing(3),
    },
  },
}));

export default function SponsorVideoCtaButton({
  text,
  link,
  position,
  color,
  textColor,
  token,
  clickPixel,
  additionalClickPixels,
  isCloseable,
  adLabel,
}) {
  const theme = useTheme();
  const resolvedPosition = POSITION_MAP[position] ?? position ?? BOTTOM_RIGHT;

  const onClick = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    createSponsorImpression(token, CREATIVE_TYPE, CLICKTHROUGH);

    trackAdPixel(clickPixel, additionalClickPixels);

    const clickId = nanoid();
    const url = link.replace("{clickId}", clickId);

    await createSponsorClick(token, clickId);
    window.open(url, "_blank");
  };

  const hasCustomBackground = Boolean(color);
  const hasCustomTextColor = Boolean(textColor);

  let style;
  if (hasCustomBackground && hasCustomTextColor) {
    style = { backgroundColor: color, color: textColor };
  } else if (hasCustomBackground) {
    style = {
      backgroundColor: color,
      color: theme.palette.primary.contrastText,
    };
  } else if (hasCustomTextColor) {
    style = { color: textColor };
  }

  return (
    <StyledButton
      onClick={onClick}
      variant="contained"
      color="primary"
      className={classNames({
        [classes.root]: true,
        [classes.topLeft]: resolvedPosition === TOP_LEFT,
        [classes.topRight]: resolvedPosition === TOP_RIGHT,
        [classes.bottomLeft]: resolvedPosition === BOTTOM_LEFT,
        [classes.bottomRight]: resolvedPosition === BOTTOM_RIGHT,
        [classes.topLeftWithAdLabel]: resolvedPosition === TOP_LEFT && adLabel,
        [classes.topRightWithClose]:
          resolvedPosition === TOP_RIGHT && isCloseable,
      })}
      style={style}
    >
      {text}
    </StyledButton>
  );
}

SponsorVideoCtaButton.propTypes = {
  text: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  position: PropTypes.oneOf([TOP_LEFT, TOP_RIGHT, BOTTOM_LEFT, BOTTOM_RIGHT]),
  color: PropTypes.string,
  textColor: PropTypes.string,
  token: PropTypes.string.isRequired,
  clickPixel: PropTypes.string,
  additionalClickPixels: PropTypes.arrayOf(PropTypes.string),
  isCloseable: PropTypes.bool,
  adLabel: PropTypes.bool,
};
